import { IMouseEvent } from "../../../base/browser/mouseEvent";
import { IDisposable } from "../../../base/common/lifecycle";
import { createDecorator } from "../../instantiation/common/registry";


export const IPointerService = createDecorator<IPointerService>('pointerService');

export interface PointerListenerOptions {
    /**
     * DOM phase to listen in. Capture (the DEFAULT) is delivered top-down BEFORE
     * bubble-phase handlers can `stopPropagation` the event — which is the whole
     * reason this service exists: the editor runs inside hostile hosts (a
     * third-party page, or the PWA dock panels) that stop pointer events on the
     * way up. Pass `capture: false` only to deliberately yield to those handlers.
     */
    capture?: boolean;
    /**
     * Pin the subscription to ONE window instead of the service-managed set of
     * open windows. Rare — e.g. force input from the main window regardless of
     * which popout is focused. Default: every managed window (so a subscription
     * keeps working after a popout without the consumer touching `window`).
     */
    window?: Window;
}

/**
 * A pointer event plus the window it was observed in. The service listens in
 * EVERY open window (so a subscription survives a popout), so a consumer that
 * owns something in ONE window — a render viewport, a panel — must know where an
 * event came from. Carrying it here keeps that a single comparison instead of
 * making every consumer re-derive it from the DOM
 * (`target.ownerDocument.defaultView`), which is easy to forget: fly mode
 * consumed and swallowed a popout window's input until it was gated.
 */
export interface IPointerEvent extends IMouseEvent {
    readonly sourceWindow: Window;
}

export type PointerListener = (event: IPointerEvent) => void;

export interface IPointerWheelEvent extends IPointerEvent {
    readonly browserEvent: WheelEvent;
}

export type PointerWheelListener = (event: IPointerWheelEvent) => void;

/**
 * Single seam for pointer input across the editor's shifting set of windows. The
 * service — not the consumer — tracks which windows are open and attaches the DOM
 * listeners; a consumer subscribes once (choosing the {@link PointerListenerOptions
 * phase}) and keeps receiving events as windows open/close (popout).
 */
export interface IPointerService {

    readonly _serviceBrand: undefined;

    onMouseDown(listener: PointerListener, options?: PointerListenerOptions): IDisposable;
    /** Browser/OS double-click gesture, delivered through the managed-window input seam. */
    onDoubleClick(listener: PointerListener, options?: PointerListenerOptions): IDisposable;
    onMouseMove(listener: PointerListener, options?: PointerListenerOptions): IDisposable;
    onMouseUp(listener: PointerListener, options?: PointerListenerOptions): IDisposable;
    onMouseCancel(listener: PointerListener, options?: PointerListenerOptions): IDisposable;
    /** Non-passive so consumers can prevent scrolling when they handle the wheel. */
    onWheel(listener: PointerWheelListener, options?: PointerListenerOptions): IDisposable;
    /**
     * A CONTEXT CLICK, through the same per-window seam as the rest.
     *
     * Nothing is prevented here: on a host page the browser's own menu belongs
     * to the page, and a subscriber that decides to show ours calls
     * `preventDefault` itself — only once it actually has something to show.
     */
    onContextMenu(listener: PointerListener, options?: PointerListenerOptions): IDisposable;

    /**
     * Whether any button in `buttonsMask` (a `MouseEvent.buttons` bitmask:
     * 1 = left, 2 = right, 4 = middle) is currently held, across ALL windows —
     * the pointer analogue of `IKeyboardService.isKeyPressed`. E.g. the gizmo
     * mode commands pass 7 so bare W/E stay tool shortcuts while a mouse-drag
     * gesture (camera look/pan, an active drag) owns the letters.
     */
    isButtonPressed(buttonsMask: number): boolean;
}
